import { useEffect, useState } from 'react';
import { getAddress, isAddressEqual, zeroAddress } from 'viem';

import { readAuthorizationWallet } from '@/features/tradingSession/walletResolution';
import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';

/**
 * Onboarding cannot open until this settles, so it must always settle. A public
 * RPC that hangs falls back to "unknown" rather than holding the dialog shut.
 */
const READ_TIMEOUT_MS = 6_000;

export interface AuthorizationWalletPresence {
    /** The read is finished, one way or another. */
    resolved: boolean;
    /** True/false once read; null when unknown (skipped, failed, timed out). */
    exists: boolean | null;
}

const UNKNOWN: AuthorizationWalletPresence = { resolved: true, exists: null };

/**
 * Reads `wallets(owner)` on the anchor's authorization registry: the durable
 * on-chain half of "how far did this user get". It is what lets a reload resume
 * the provisioning ceremony on the exact leg that is still outstanding, without
 * asking the wallet to sign anything to find out.
 */
export function useAuthorizationWalletPresence(
    address: string | undefined,
    enabled: boolean,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): AuthorizationWalletPresence {
    const [presence, setPresence] =
        useState<AuthorizationWalletPresence>(UNKNOWN);

    useEffect(() => {
        if (!address || !enabled) {
            setPresence(UNKNOWN);
            return;
        }
        let cancelled = false;
        let timer: number | undefined;
        setPresence({ resolved: false, exists: null });
        const timeout = new Promise<null>((resolve) => {
            timer = window.setTimeout(() => resolve(null), READ_TIMEOUT_MS);
        });
        void Promise.race([
            readAuthorizationWallet(getAddress(address), anchorChainId).catch(
                () => null,
            ),
            timeout,
        ]).then((wallet) => {
            if (cancelled) return;
            setPresence({
                resolved: true,
                exists: wallet ? !isAddressEqual(wallet, zeroAddress) : null,
            });
        });
        return () => {
            cancelled = true;
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [address, anchorChainId, enabled]);

    return presence;
}
